import type {
  CreateParticipantBehaviourRecordingRequest,
  Method,
  ParticipantBehaviour,
} from '@piccolohealth/pbs-common';
import { ChakraV3, FloatingPopover, ScrollArea } from '@piccolohealth/ui';
import { DateTime, dateTimeSchema, P, z, zodNonEmptyString } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { useCreateParticipantBehaviourRecordingMutation } from '../../../graphql/hooks/useCreateParticipantBehaviourRecordingMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { ParticipantBehaviourRecordingForm } from './ParticipantBehaviourRecordingForm';

type FormValues = CreateParticipantBehaviourRecordingRequest & {
  participantBehaviourId: string;
  method: Method['__typename'];
};

export const participantBehaviourRecordingCreateSchema: z.ZodType<FormValues> =
  z.discriminatedUnion('method', [
    z.object({
      participantBehaviourId: z.string({}),
      timestamp: dateTimeSchema,
      method: z.literal('FrequencyMethod'),
      payload: z.object({
        frequency: z.object({
          value: z.number('Frequency is required'),
        }),
      }),
    }),
    z.object({
      participantBehaviourId: z.string({}),
      timestamp: dateTimeSchema,
      method: z.literal('DurationMethod'),
      payload: z.object({
        duration: z.object({
          value: z.number(),
        }),
      }),
    }),
    z.object({
      participantBehaviourId: z.string({}),
      timestamp: dateTimeSchema,
      method: z.literal('AbcMethod'),
      payload: z.object({
        abc: z.object({
          antecedent: zodNonEmptyString(),
          behaviour: zodNonEmptyString(),
          consequence: zodNonEmptyString(),
        }),
      }),
    }),
    z.object({
      participantBehaviourId: z.string({}),
      timestamp: dateTimeSchema,
      method: z.literal('EpisodicSeverityMethod'),
      payload: z.object({
        episodicSeverity: z.object({
          level: z.number(),
        }),
      }),
    }),
  ]);

interface ParticipantBehaviourRecordingCreateContentProps {
  participantId: string;
  participantBehaviours: ParticipantBehaviour[];
  onClose: () => void;
}

const ParticipantBehaviourRecordingCreateContent = (
  props: ParticipantBehaviourRecordingCreateContentProps,
) => {
  const { participantId, participantBehaviours, onClose } = props;

  const { organization, successToast, errorToast } = useAppContext();

  const mutation = useCreateParticipantBehaviourRecordingMutation();

  const defaultValues = React.useMemo(() => {
    const defaultBehaviour = P.first(participantBehaviours);

    if (P.isNil(defaultBehaviour)) {
      return {
        timestamp: DateTime.now(),
      };
    }

    const enabledMethods = Object.values(defaultBehaviour.methods).filter(
      (m) => !P.isString(m) && m.enabled,
    ) as Method[];

    const [enabledMethod] = enabledMethods;

    if (P.isUndefined(enabledMethod)) {
      return {
        participantBehaviourId: defaultBehaviour.id,
        timestamp: DateTime.now(),
      };
    }

    const defaultMethod = defaultBehaviour.methods.frequency.enabled
      ? (defaultBehaviour.methods.frequency.__typename ?? 'FrequencyMethod')
      : (enabledMethod.__typename ?? 'FrequencyMethod');

    return {
      participantBehaviourId: defaultBehaviour.id,
      method: defaultMethod,
      timestamp: DateTime.now(),
    };
  }, [participantBehaviours]);

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(participantBehaviourRecordingCreateSchema),
    mode: 'all',
  });

  const { reset } = methods;

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const payload = P.run(() => {
        switch (values.method) {
          case 'AbcMethod':
            return { abc: values.payload.abc };
          case 'FrequencyMethod':
            return { frequency: values.payload.frequency };
          case 'DurationMethod':
            return { duration: values.payload.duration };
          case 'EpisodicSeverityMethod':
            return { episodicSeverity: values.payload.episodicSeverity };
          default:
            return {};
        }
      });

      await mutation
        .mutateAsync({
          organizationId: organization.id,
          participantId: participantId,
          participantBehaviourId: values.participantBehaviourId,
          request: {
            payload,
            timestamp: values.timestamp,
          },
        })
        .then(async () => {
          successToast('Participant behaviour recording created successfully');
          onClose();
          reset();
        })
        .catch((err) => {
          errorToast(`Error creating participant behaviour recording: ${err.message}`);
        });
    },
    [mutation, organization.id, participantId, successToast, onClose, reset, errorToast],
  );

  return (
    <ChakraV3.Stack minH='xs' w='xl' bg='white' gap={0} shadow='2xl' rounded='xl'>
      <ChakraV3.HStack px={3} py={3} borderBottomWidth='1px'>
        <ChakraV3.Text fontSize='md' fontWeight='semibold'>
          Add recording
        </ChakraV3.Text>
      </ChakraV3.HStack>

      <FormProvider {...methods}>
        <ScrollArea maxH='3xl' h='full'>
          <ChakraV3.Stack as='form' px={4} py={4} gap={8} onSubmit={methods.handleSubmit(onSubmit)}>
            <ParticipantBehaviourRecordingForm participantBehaviours={participantBehaviours} />
            <HookedSubmitButton disabled={false}>
              <FaPlus />
              Add data
            </HookedSubmitButton>
          </ChakraV3.Stack>
        </ScrollArea>
      </FormProvider>
    </ChakraV3.Stack>
  );
};

interface Props extends ChakraV3.ButtonProps {
  participantId: string;
  participantBehaviours: ParticipantBehaviour[];
}

export const ParticipantBehaviourRecordingCreateButton = (props: Props) => {
  const { participantId, participantBehaviours, children, ...rest } = props;

  const disclosure = ChakraV3.useDisclosure();

  return (
    <FloatingPopover
      placement='left'
      open={disclosure.open}
      setOpen={(isOpen) => (isOpen ? disclosure.onOpen() : disclosure.onClose())}
      render={() => (
        <ParticipantBehaviourRecordingCreateContent
          participantId={participantId}
          participantBehaviours={participantBehaviours}
          onClose={disclosure.onClose}
        />
      )}
    >
      <ChakraV3.Button size='sm' {...rest} variant='subtle'>
        <FaPlus />
        {children}
      </ChakraV3.Button>
    </FloatingPopover>
  );
};
